// src/pages/TransferCreateDualBucket.jsx
import React, { useState, useMemo, useEffect, useRef } from "react";
import Select from "react-select";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  FiBox,
  FiSend,
  FiSearch,
  FiRefreshCcw,
  FiArrowRight,
  FiArrowLeft,
  FiFilter,
  FiX,
} from "react-icons/fi";
import { FaArrowRightArrowLeft } from "react-icons/fa6";

import { useAddTransferMutation } from "../toolkitServices/transferApi";
import { useGetCountersQuery, useGetCountersAllQuery } from "../toolkitServices/counterApi";
import { useGetAllBranchesQuery, useGetAllBranchesAllQuery } from "../toolkitServices/branchApi";
import { useGetAllLabelledStockQuery } from "../toolkitServices/stockApi";
import { useGetAllEmployeesAllQuery } from "../toolkitServices/employeeApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import { useGetAllProductsQuery } from "../toolkitServices/productApi";
import { useGetAllDesignsQuery } from "../toolkitServices/designApi";
import { useGetAllPuritiesQuery } from "../toolkitServices/purityApi";

/* ------------ Tiny reusable spinner ------------ */
const Spinner = ({ size = 16, className = "" }) => (
  <span
    className={`inline-block animate-spin rounded-full border-2 border-current border-t-transparent ${className}`}
    style={{ width: size, height: size }}
  />
);

// debounce
const useDebounce = (value, delay = 500) => {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
};

// media query
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(false);
  useEffect(() => {
    const m = window.matchMedia(query);
    const onChange = () => setMatches(!!m.matches);
    onChange();
    m.addEventListener?.("change", onChange);
    return () => m.removeEventListener?.("change", onChange);
  }, [query]);
  return matches;
};

/* ------------ react-select styles ------------ */
const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    height: 38,
    borderRadius: 14,
    borderColor: state.isFocused ? "#7c3aed" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 3px #7c3aed1f" : "none",
    "&:hover": { borderColor: "#7c3aed" },
    fontSize: "0.85rem",
    backgroundColor: "white",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 10px" }),
  indicatorsContainer: (base) => ({ ...base, height: 38 }),
  placeholder: (base) => ({ ...base, color: "#94a3b8", fontSize: "0.85rem" }),
  singleValue: (base) => ({
    ...base,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    color: "#0f172a",
  }),
  menu: (base) => ({ ...base, zIndex: 50 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
};

// table classes
const thCls =
  "px-3 py-2 text-[10px] sm:text-[11px] font-semibold uppercase tracking-wide whitespace-nowrap";
const tdCls = "px-3 py-2 text-[11px] sm:text-xs align-middle";
const chipCls = "inline-flex items-center gap-1 rounded-full bg-white/12 px-2 py-1 text-[10px]";

// ✅ fixed height for BOTH tables (scroll only inside table area)
const tableScrollCls = "min-h-0 overflow-auto h-[340px] sm:h-[380px]  md:h-[168px] ";

const TransferCreateDualBucket = () => {
  const navigate = useNavigate();
  const user = useSelector((s) => s.auth?.user);

  const tenantId = user?.tenantId;
  const limit = 100;

  const isStaff = (user?.role || "").toLowerCase() === "staff";

  const [transferMode, setTransferMode] = useState("counter");

  const emp = user?.employeeDetails || null;
  const myEmployeeId = user?.employeeId || emp?._id || null;
  const myEmployeeName = emp
    ? `${emp.firstName || ""} ${emp.lastName || ""}`.trim() ||
      emp.officialEmail ||
      emp.email ||
      "Employee"
    : user?.userName || "Employee";

  const allowedBranchIds = useMemo(() => {
    const arr = emp?.branches?.length ? emp.branches : emp?.branch ? [emp.branch] : [];
    return arr.map(String);
  }, [emp]);

  const allowedCounterIds = useMemo(() => {
    const arr = emp?.counters?.length ? emp.counters : emp?.counter ? [emp.counter] : [];
    return arr.map(String);
  }, [emp]);

  // form states
  const [fromBranch, setFromBranch] = useState(null);
  const [toBranch, setToBranch] = useState(null);
  const [fromCounter, setFromCounter] = useState(null);
  const [toCounter, setToCounter] = useState(null);
  const [fromEmployee, setFromEmployee] = useState(null);
  const [toEmployee, setToEmployee] = useState(null);

  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({ category: "", product: "", design: "", purity: "" });

  const debouncedSearch = useDebounce(search);
  const [page, setPage] = useState(1);

  const [selectedItems, setSelectedItems] = useState([]);
  const [checkedLeft, setCheckedLeft] = useState([]);
  const [checkedRight, setCheckedRight] = useState([]);

  const isSmUp = useMediaQuery("(min-width: 640px)");

  // ✅ NEW: modal for filters (all screens)
  const [showFilterModal, setShowFilterModal] = useState(false);

  useEffect(() => {
    if (!showFilterModal) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowFilterModal(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showFilterModal]);

  /* =========================
      QUERIES
  ========================= */
  const { data: branchesFromData } = useGetAllBranchesQuery(
    { page: 1, limit: 500, search: "" },
    { skip: !tenantId }
  );

  const { data: branchesAllData } = useGetAllBranchesAllQuery(
    { page: 1, limit: 1000, search: "" },
    { skip: !tenantId }
  );

  const { data: countersFromData } = useGetCountersQuery(
    { page: 1, limit: 1000, search: "" },
    { skip: !tenantId }
  );

  const { data: countersAllData } = useGetCountersAllQuery(
    { page: 1, limit: 3000, search: "" },
    { skip: !tenantId }
  );

  const shouldFetchFromEmployees = !!(!isStaff && (fromBranch?.value || fromCounter?.value));
  const shouldFetchToEmployees = !!(toBranch?.value || toCounter?.value);

  const { data: fromEmployeesResp } = useGetAllEmployeesAllQuery(
    {
      page: 1,
      limit: 500,
      search: "",
      branch: fromBranch?.value || undefined,
      counter: fromCounter?.value || undefined,
    },
    { skip: !tenantId || !shouldFetchFromEmployees }
  );

  const { data: toEmployeesResp } = useGetAllEmployeesAllQuery(
    {
      page: 1,
      limit: 500,
      search: "",
      branch: toBranch?.value || undefined,
      counter: toCounter?.value || undefined,
    },
    { skip: !tenantId || !shouldFetchToEmployees }
  );

  const { data: catResp } = useGetAllCategoriesQuery({ page: 1, limit: 200, search: "" });
  const { data: productResp } = useGetAllProductsQuery({
    page: 1,
    limit: 200,
    search: "",
    categoryId: filters.category || undefined,
  });
  const { data: designResp } = useGetAllDesignsQuery({
    page: 1,
    limit: 200,
    search: "",
    categoryId: filters.category || undefined,
    productId: filters.product || undefined,
  });
  const { data: purityResp } = useGetAllPuritiesQuery({
    page: 1,
    limit: 200,
    search: "",
    category: filters.category || undefined,
  });

  const categories = catResp?.data || [];
  const products = productResp?.data || [];
  const designs = designResp?.data || [];
  const purities = purityResp?.data || [];

  /* =========================
      BRANCH OPTIONS
  ========================= */
  const fromBranches = useMemo(() => {
    const list = (branchesFromData?.data || []).map((b) => ({
      label: b.branchName,
      value: String(b._id),
    }));
    return list
    // if (!isStaff) return list;
    // return list.filter((b) => allowedBranchIds.includes(String(b.value)));
  }, [branchesFromData, isStaff, allowedBranchIds]);

  const toBranches = useMemo(() => {
    return (branchesAllData?.data || []).map((b) => ({
      label: b.branchName,
      value: String(b._id),
    }));
  }, [branchesAllData]);

  /* =========================
      COUNTERS OPTIONS
  ========================= */
  const restrictedCounters = useMemo(() => countersFromData?.data || [], [countersFromData]);
  const allTenantCounters = useMemo(() => countersAllData?.data || [], [countersAllData]);

  const getCounterBranchId = (c) => String(c?.branchId?._id ?? c?.branchId ?? "");

  const filteredFromCounters = useMemo(() => {
    if (!fromBranch) return [];
    let list = countersFromData?.data?.filter((c) => getCounterBranchId(c) === String(fromBranch?.value));

    // if (isStaff && allowedCounterIds.length) {
    //   list = list.filter((c) => allowedCounterIds.includes(String(c._id)));
    // }

    return list.map((c) => ({ label: c.counterName, value: String(c._id) }));
  }, [fromBranch?.value, countersFromData, isStaff, allowedCounterIds]);

  const filteredToCounters = useMemo(() => {
    if (!toBranch) return [];
    let list = allTenantCounters.filter((c) => getCounterBranchId(c) === String(toBranch.value));

    if (String(toBranch.value) === String(fromBranch?.value || "")) {
      list = list.filter((c) => String(c._id) !== String(fromCounter?.value || ""));
    }

    return list.map((c) => ({ label: c.counterName, value: String(c._id) }));
  }, [toBranch, allTenantCounters, fromBranch?.value, fromCounter?.value]);

  /* =========================
      RESET LOGIC
  ========================= */
  useEffect(() => {
    if (isStaff) return;
    setFromCounter(null);
    setFromEmployee(null);
  }, [fromBranch, isStaff]);

  useEffect(() => {
    setToCounter(null);
    setToEmployee(null);
  }, [toBranch]);

  /* =========================
      EMPLOYEE OPTIONS
  ========================= */
  const employees = useMemo(() => {
    if (isStaff) {
      return myEmployeeId ? [{ label: myEmployeeName, value: String(myEmployeeId) }] : [];
    }
    const rows = fromEmployeesResp?.data || [];
    return rows.map((e) => ({
      label:
        `${e.firstName || ""} ${e.lastName || ""}`.trim() ||
        e.officialEmail ||
        e.email ||
        "Employee",
      value: String(e._id),
    }));
  }, [isStaff, myEmployeeId, myEmployeeName, fromEmployeesResp]);

  const toEmployees = useMemo(() => {
    const rows = toEmployeesResp?.data || [];
    return rows.map((e) => ({
      label:
        `${e.firstName || ""} ${e.lastName || ""}`.trim() ||
        e.officialEmail ||
        e.email ||
        "Employee",
      value: String(e._id),
    }));
  }, [toEmployeesResp]);

  /* =========================
      STAFF INITIAL DEFAULTS
  ========================= */
  const didInitRef = useRef(false);

  useEffect(() => {
    if (didInitRef.current) return;
    if (!tenantId) return;
    if (!fromBranches.length) return;

    if (isStaff) {
      if (!fromEmployee && myEmployeeId) {
        setFromEmployee({ label: myEmployeeName, value: String(myEmployeeId) });
      }

      const preferredBranchId = String(emp?.branch || allowedBranchIds?.[0] || "");
      const bOpt =
        fromBranches.find((b) => String(b.value) === preferredBranchId) || fromBranches[0] || null;

      if (bOpt && !fromBranch) setFromBranch(bOpt);
      if (!toBranch && bOpt) setToBranch(bOpt);

      didInitRef.current = true;
    } else {
      didInitRef.current = true;
    }
  }, [
    tenantId,
    isStaff,
    fromBranches,
    fromBranch,
    toBranch,
    fromEmployee,
    myEmployeeId,
    myEmployeeName,
    emp?.branch,
    allowedBranchIds,
  ]);

  useEffect(() => {
    if (!isStaff) return;
    if (!fromBranch?.value) return;
    if (fromCounter?.value) return;

    const preferredCounterId = String(emp?.counter || allowedCounterIds?.[0] || "");
    const cOpt =
      filteredFromCounters.find((c) => String(c.value) === preferredCounterId) ||
      filteredFromCounters[0] ||
      null;

    if (cOpt) setFromCounter(cOpt);
  }, [
    isStaff,
    fromBranch?.value,
    filteredFromCounters,
    fromCounter?.value,
    emp?.counter,
    allowedCounterIds,
  ]);

  useEffect(() => {
    if (!isStaff) return;
    if (!toBranch?.value) return;
    if (toCounter?.value) return;

    const first = filteredToCounters[0] || null;
    if (first) setToCounter(first);
  }, [isStaff, toBranch?.value, filteredToCounters, toCounter?.value]);

  useEffect(() => {
    if (isStaff) return;
    setFromEmployee(null);
  }, [fromCounter, isStaff]);

  useEffect(() => {
    setToEmployee(null);
  }, [toCounter]);

  const prevFromBranchRef = useRef(null);
  useEffect(() => {
    const prev = prevFromBranchRef.current;
    const curr = fromBranch;

    if (!prev && curr) {
      if (!toBranch) setToBranch(curr);
      prevFromBranchRef.current = curr;
      return;
    }

    if (prev?.value && curr?.value && String(prev.value) !== String(curr.value)) {
      if (!toBranch || String(toBranch.value) === String(prev.value)) {
        setToBranch(curr);
      }
    }

    prevFromBranchRef.current = curr;
  }, [fromBranch, toBranch]);

  /* =========================
      MODE LOGIC
  ========================= */
  useEffect(() => {
    if (transferMode !== "counter") return;
    if (!fromBranch) return;
    if (!toBranch || String(toBranch.value) !== String(fromBranch.value)) {
      setToBranch(fromBranch);
    }
  }, [transferMode, fromBranch, toBranch]);

  useEffect(() => {
    setToCounter(null);
    setToEmployee(null);
  }, [transferMode]);

  /* =========================
      STOCK QUERY
  ========================= */
  const shouldFetchStock = !!(fromBranch?.value && fromCounter?.value);

  const queryArgs = useMemo(
    () => ({
      tenantId,
      branchId: fromBranch?.value,
      counterId: fromCounter?.value,
      status: "active",
      page,
      limit,
      search: debouncedSearch,
      categoryId: filters.category || "",
      productId: filters.product || "",
      designId: filters.design || "",
      purityId: filters.purity || "",
    }),
    [tenantId, fromBranch, fromCounter, page, limit, debouncedSearch, filters]
  );

  const { data: stockData, isFetching, refetch } = useGetAllLabelledStockQuery(queryArgs, {
    skip: !shouldFetchStock,
  });

  useEffect(() => {
    if (shouldFetchStock) refetch();
  }, [shouldFetchStock, fromBranch, fromCounter, refetch]);

  const items = stockData?.data || [];
  const totalPages = stockData?.pagination?.totalPages || stockData?.pages || 1;

  const filteredItems = useMemo(
    () => items.filter((it) => !selectedItems.some((s) => s._id === it._id)),
    [items, selectedItems]
  );

  const [addTransfer, { isLoading }] = useAddTransferMutation();

  /* =========================
      CHECKBOX / MOVE
  ========================= */
  const rightBucketRef = useRef(null);
  const leftBucketRef = useRef(null);

  const toggleCheckLeft = (id) =>
    setCheckedLeft((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleCheckRight = (id) =>
    setCheckedRight((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const moveSelectedLeftToRight = () => {
    const selected = filteredItems.filter((it) => checkedLeft.includes(it._id));
    if (!selected.length) return;
    setSelectedItems((p) => [...p, ...selected]);
    setCheckedLeft([]);

    if (!isSmUp) {
      setTimeout(() => {
        rightBucketRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  };

  const moveSelectedRightToLeft = () => {
    if (!checkedRight.length) return;
    setSelectedItems((p) => p.filter((it) => !checkedRight.includes(it._id)));
    setCheckedRight([]);

    if (!isSmUp) {
      setTimeout(() => {
        leftBucketRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  };

  const toggleSelectAllLeft = () => {
    const allIds = filteredItems.map((it) => it._id);
    setCheckedLeft(allIds.length === checkedLeft.length ? [] : allIds);
  };
  const toggleSelectAllRight = () => {
    const allIds = selectedItems.map((it) => it._id);
    setCheckedRight(allIds.length === checkedRight.length ? [] : allIds);
  };

  /* =========================
      SUBMIT
  ========================= */
  const handleSubmit = async () => {
    const required = [fromBranch, fromCounter, fromEmployee, toBranch, toCounter, toEmployee];
    if (required.some((x) => !x)) {
      toast.error("⚠ Please fill all required fields before creating transfer");
      return;
    }
    if (selectedItems.length === 0) {
      toast.error("Please select at least one item to transfer");
      return;
    }

    const payload = {
      tenantId,
      transferType: transferMode === "branch" ? "branch" : "counter",
      fromBranchId: fromBranch.value,
      fromBranchName: fromBranch.label,
      toBranchId: toBranch.value,
      toBranchName: toBranch.label,
      fromCounterId: fromCounter.value,
      fromCounterName: fromCounter.label,
      toCounterId: toCounter.value,
      toCounterName: toCounter.label,
      fromUserId: fromEmployee.value,
      fromUserName: fromEmployee.label,
      toUserId: toEmployee.value,
      toUserName: toEmployee.label,
      items: selectedItems.map((it) => ({
        itemCode: it.itemCode,
        categoryName: it.categoryName,
        productName: it.productName,
        designName: it.designName,
        purityName: it.purityName,
        grossWt: it.grossWt,
        netWt: it.netWt,
        stoneWt: it.stoneWt,
        diamondWt: it.diamondWt,
        mrp: it.mrp,
      })),
    };

    try {
      await addTransfer(payload).unwrap();
      toast.success("✅ Transfer created successfully");
      navigate("/transfers");
    } catch (err) {
      toast.error(err?.data?.message || "Failed to create transfer");
    }
  };

  const handleResetFilters = () => {
    setFilters({ category: "", product: "", design: "", purity: "" });
    setSearch("");
    setPage(1);
  };

  /* =========================
      FILTER DROPDOWN OPTIONS
  ========================= */
  const categoryOptions = categories.map((c) => ({ label: c.name, value: c._id }));
  const productOptions = products.map((p) => ({ label: p.name, value: p._id }));
  const designOptions = designs.map((d) => ({ label: d.designName, value: d._id }));
  const purityOptions = purities.map((p) => ({ label: p.purity, value: p._id }));

  const disableFromTo = selectedItems?.length > 0;
  const isFromEmployeeLocked = isStaff || disableFromTo;
  const isToBranchDisabled = disableFromTo || transferMode === "counter" || !fromBranch;

  const checkedTotal = checkedLeft.length + checkedRight.length;

  return (
    <div className="relative bg-white shadow-lg rounded-2xl border border-slate-200 flex flex-col overflow-hidden h-[calc(100vh-140px)] md:h-[calc(100vh-70px)]">
      {isFetching && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-white/55">
          <div className="flex flex-col items-center gap-2 text-[#6b59c6] text-xs sm:text-sm">
            <Spinner size={26} />
            <span>Loading stock…</span>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div className="shrink-0  bg-[#9481ce] text-white border-b border-white/10">
        <div className="px-3 sm:px-4 pt-2 pb-2">
          <div className="flex items-start sm:items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="h-8 w-8 sm:h-9 sm:w-9 rounded-2xl bg-white/15 flex items-center justify-center shrink-0">
                <FaArrowRightArrowLeft size={15} />
              </span>
              <div className="min-w-0">
                <h2 className="text-[14px] sm:text-[17px] font-semibold tracking-tight truncate">
                  Stock Transfer
                </h2>
                <p className="text-[10px] sm:text-[11px] text-violet-100/90 truncate">
                  Source → Destination • Move labelled stock
                </p>
              </div>

              {isFetching && (
                <span className="hidden sm:inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10 shrink-0">
                  <Spinner size={10} /> refreshing…
                </span>
              )}
            </div>

            <div className="hidden sm:flex items-center gap-2 shrink-0">
              <div className="inline-flex items-center gap-1 p-[2px] rounded-full bg-white/15 border border-white/15">
                <button
                  type="button"
                  onClick={() => setTransferMode("counter")}
                  className={`whitespace-nowrap px-3 py-1 rounded-full text-[11px] font-semibold transition ${
                    transferMode === "counter"
                      ? "bg-white text-[#5a46c8] shadow-sm"
                      : "text-white/90 hover:bg-white/10"
                  }`}
                >
                  Counter ↔ Counter
                </button>
                <button
                  type="button"
                  onClick={() => setTransferMode("branch")}
                  className={`whitespace-nowrap px-3 py-1 rounded-full text-[11px] font-semibold transition ${
                    transferMode === "branch"
                      ? "bg-white text-[#5a46c8] shadow-sm"
                      : "text-white/90 hover:bg-white/10"
                  }`}
                >
                  Branch ↔ Branch
                </button>
              </div>

              <div className="hidden lg:flex items-center gap-2 ml-2">
                <span className={chipCls}>
                  Selected: <span className="font-semibold">{selectedItems.length}</span>
                </span>
                <span className={chipCls}>
                  Checked: <span className="font-semibold">{checkedTotal}</span>
                </span>
              </div>
            </div>
          </div>

          <div className="sm:hidden mt-2">
            <div className="inline-flex items-center gap-1 p-[2px] rounded-full bg-white/15 border border-white/15 w-full">
              <button
                type="button"
                onClick={() => setTransferMode("counter")}
                className={`flex-1 px-3 py-1 rounded-full text-[11px] font-semibold transition ${
                  transferMode === "counter"
                    ? "bg-white text-[#5a46c8] shadow-sm"
                    : "text-white/90 hover:bg-white/10"
                }`}
              >
                Counter ↔ Counter
              </button>
              <button
                type="button"
                onClick={() => setTransferMode("branch")}
                className={`flex-1 px-3 py-1 rounded-full text-[11px] font-semibold transition ${
                  transferMode === "branch"
                    ? "bg-white text-[#5a46c8] shadow-sm"
                    : "text-white/90 hover:bg-white/10"
                }`}
              >
                Branch ↔ Branch
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* BODY */}
      <div className="flex-1 min-h-0 overflow-auto md:overflow-hidden">
        {/* FROM / TO */}
        <div className="shrink-0 p-2">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="bg-slate-50 p-3 rounded-2xl border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
                  From (Source)
                </p>
                <span className="text-[10px] text-slate-500">
                  {disableFromTo ? "Locked (items selected)" : isStaff ? "Staff mode" : "Editable"}
                </span>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Branch *</label>
                  <Select
                    styles={selectStyles}
                    options={fromBranches}
                    value={fromBranch}
                    onChange={(v) => {
                      setFromBranch(v);
                      setPage(1);
                      setSelectedItems([]);
                      setCheckedLeft([]);
                      setCheckedRight([]);
                    }}
                    isDisabled={disableFromTo}
                    placeholder="Select branch"
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Counter *</label>
                  <Select
                    styles={selectStyles}
                    options={filteredFromCounters}
                    value={fromCounter}
                    onChange={(v) => {
                      setFromCounter(v);
                      setPage(1);
                      setSelectedItems([]);
                      setCheckedLeft([]);
                      setCheckedRight([]);
                    }}
                    placeholder="Select counter"
                    isDisabled={!fromBranch || disableFromTo}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Employee *</label>
                  <Select
                    styles={selectStyles}
                    options={employees}
                    value={fromEmployee}
                    onChange={setFromEmployee}
                    isDisabled={isFromEmployeeLocked}
                    placeholder="Select employee"
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-3 rounded-2xl border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <p className="text-[11px] font-semibold text-slate-700 uppercase tracking-wide">
                  To (Destination)
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Branch *</label>
                  <Select
                    styles={selectStyles}
                    options={toBranches}
                    value={toBranch}
                    onChange={setToBranch}
                    isDisabled={isToBranchDisabled}
                    placeholder={transferMode === "counter" ? "Same as source" : "Select branch"}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Counter *</label>
                  <Select
                    styles={selectStyles}
                    options={filteredToCounters}
                    value={toCounter}
                    onChange={setToCounter}
                    placeholder="Select counter"
                    isDisabled={!toBranch || disableFromTo}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Employee *</label>
                  <Select
                    styles={selectStyles}
                    options={toEmployees}
                    value={toEmployee}
                    onChange={setToEmployee}
                    placeholder="Select employee"
                    isDisabled={disableFromTo}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* BUCKETS */}
        <div className="flex-1 min-h-0 flex flex-col md:grid md:grid-cols-2 gap-3 p-2 pt-0">
          {/* LEFT */}
          <div
            ref={leftBucketRef}
            className="border border-slate-200 rounded-2xl bg-white flex flex-col flex-1 min-h-0 shadow-[0_1px_0_rgba(15,23,42,0.03)]"
          >
            <div className="shrink-0 bg-gradient-to-r from-[#ece9ff] to-[#f7f5ff] text-[#4b3d91] px-4 py-2 flex justify-between items-center border-b border-slate-200 rounded-t-2xl">
              <div className="flex items-center gap-2 min-w-0 ">
                <span className="h-8 w-8 rounded-2xl bg-white/90 flex items-center justify-center shrink-0 shadow-sm">
                  <FiBox size={15} />
                </span>
                <div className="leading-tight min-w-0">
                  <p className="font-semibold text-[13px] truncate">Available Items</p>
                  <p className="text-[11px] text-[#4b3d91]/70 truncate">
                    {shouldFetchStock ? `Showing ${filteredItems.length} items` : "Select branch & counter"}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => shouldFetchStock && refetch()}
                  disabled={!shouldFetchStock}
                  className="inline-flex items-center gap-1 text-[11px] px-3 py-1 rounded-full bg-white/90 hover:bg-white border border-white/60 disabled:opacity-50"
                  title="Refresh"
                >
                  <FiRefreshCcw size={13} /> <span className="hidden sm:inline">Refresh</span>
                </button>

                <button
                  onClick={toggleSelectAllLeft}
                  disabled={!filteredItems.length}
                  className="text-[11px] px-3 py-1 rounded-full bg-white/90 hover:bg-white border border-white/60 disabled:opacity-50"
                  title="Select all available"
                >
                  {checkedLeft.length === filteredItems.length && filteredItems.length > 0
                    ? "Unselect"
                    : "Select all"}
                </button>
              </div>
            </div>

            {/* Filters (no inline fields on desktop; open modal instead) */}
            <div className="shrink-0 px-3 sm:px-4 py-2 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <div className="flex items-center border border-slate-200 rounded-2xl px-3 py-2 bg-white w-full">
                  <FiSearch className="text-slate-400 mr-2" size={14} />
                  <input
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Search item code / product…"
                    className="outline-none text-[12px] w-full bg-transparent"
                  />
                </div>

                {/* ✅ Filter icon now visible on desktop too */}
                <button
                  type="button"
                  onClick={() => setShowFilterModal(true)}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-2xl border border-slate-200 bg-white text-[12px] font-semibold"
                  title="Filters"
                >
                  <FiFilter />
                </button>

                <div className="hidden sm:flex items-center gap-2">
                  <button
                    onClick={handleResetFilters}
                    className="inline-flex items-center gap-2 px-3 py-2 text-[12px] rounded-full border border-slate-200 bg-white hover:bg-slate-100"
                  >
                    <FiRefreshCcw size={14} /> Reset
                  </button>

                  <button
                    onClick={moveSelectedLeftToRight}
                    disabled={!checkedLeft.length}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-[#6b59c6] text-white text-[12px] font-semibold shadow-sm hover:bg-[#5a48a8] disabled:opacity-50"
                  >
                    Move <FiArrowRight />
                  </button>
                </div>
              </div>

              <div className="sm:hidden mt-2">
                <button
                  onClick={moveSelectedLeftToRight}
                  disabled={!checkedLeft.length}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-2xl bg-[#6b59c6] text-white text-[12px] font-semibold shadow-sm hover:bg-[#5a48a8] disabled:opacity-50"
                >
                  Move Selected <FiArrowRight />
                </button>
              </div>
            </div>

            {/* ✅ Table Area fixed height + scroll */}
            <div className={tableScrollCls}>
              <table className="w-full table-auto">
                <thead className="bg-[#f2efff] text-[#4b3d91] sticky top-0 z-10 border-b border-slate-200">
                  <tr>
                    <th className={`${thCls} w-11 text-center`}>☑</th>
                    <th className={thCls}>Code</th>
                    <th className={`${thCls} text-right w-24`}>Net Wt</th>
                    <th className={`${thCls} text-right w-24 hidden sm:table-cell`}>MRP</th>
                    <th className={`${thCls} hidden sm:table-cell`}>Product</th>
                  </tr>
                </thead>

                <tbody className="text-gray-700">
                  {!shouldFetchStock ? (
                    <tr>
                      <td colSpan={5} className="text-center py-10 text-sm text-slate-500">
                        Select branch & counter to load stock.
                      </td>
                    </tr>
                  ) : isFetching ? (
                    <tr>
                      <td colSpan={5} className="text-center py-10 text-sm text-slate-500">
                        Loading items…
                      </td>
                    </tr>
                  ) : filteredItems.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-10 text-sm text-slate-400">
                        No items found with current filters.
                      </td>
                    </tr>
                  ) : (
                    filteredItems.map((it) => (
                      <tr key={it._id} className="border-t border-slate-100 hover:bg-[#f6f2ff] transition">
                        <td className={`${tdCls} text-center`}>
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-[#6b59c6]"
                            checked={checkedLeft.includes(it._id)}
                            onChange={() =>
                              setCheckedLeft((p) =>
                                p.includes(it._id) ? p.filter((x) => x !== it._id) : [...p, it._id]
                              )
                            }
                          />
                        </td>

                        <td className={`${tdCls} font-semibold text-[#6b59c6]`}>
                          <div className="flex flex-col leading-tight min-w-0">
                            <span className="truncate">{it.itemCode}</span>
                            <span className="sm:hidden text-[10px] font-medium text-slate-500 truncate">
                              {it.productName || "-"}
                            </span>
                          </div>
                        </td>

                        <td className={`${tdCls} text-right`}>
                          <div className="flex flex-col items-end leading-tight">
                            <span className="font-semibold text-slate-700">{it.netWt ?? "-"}</span>
                            <span className="sm:hidden text-[10px] text-slate-500">
                              {it.mrp != null ? `₹${it.mrp}` : "-"}
                            </span>
                          </div>
                        </td>

                        <td className={`${tdCls} text-right hidden sm:table-cell whitespace-nowrap`}>
                          {it.mrp != null ? `₹${it.mrp}` : "-"}
                        </td>

                        <td className={`${tdCls} hidden sm:table-cell`}>
                          <span className="block truncate">{it.productName || "-"}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination footer */}
            <div className="shrink-0 border-t border-slate-200 px-4 py-2 flex items-center justify-between bg-slate-50 rounded-b-2xl">
              {shouldFetchStock && totalPages > 1 ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="px-3 py-1 text-[12px] rounded-xl border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40"
                  >
                    Prev
                  </button>
                  <span className="text-[12px] text-slate-600">
                    Page <span className="font-semibold">{page}</span> /{" "}
                    <span className="font-semibold">{totalPages}</span>
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="px-3 py-1 text-[12px] rounded-xl border border-slate-200 bg-white hover:bg-slate-100 disabled:opacity-40"
                  >
                    Next
                  </button>
                </div>
              ) : (
                <span className="text-[12px] text-slate-500">—</span>
              )}

              <span className="text-[12px] text-slate-600">
                Checked: <span className="font-semibold">{checkedLeft.length}</span>
              </span>
            </div>
          </div>

          {/* RIGHT */}
          <div
            ref={rightBucketRef}
            className="border border-slate-200 rounded-2xl overflow-hidden bg-white flex flex-col flex-1 min-h-0 shadow-[0_1px_0_rgba(15,23,42,0.03)]"
          >
            <div className="shrink-0 bg-gradient-to-r from-[#ece9ff] to-[#f7f5ff] text-[#4b3d91] px-4 py-2 flex justify-between items-center border-b border-slate-200">
              <div className="flex items-center gap-2 min-w-0">
                <span className="h-8 w-8 rounded-2xl bg-white/90 flex items-center justify-center shrink-0 shadow-sm">
                  <FiSend size={15} />
                </span>
                <div className="leading-tight min-w-0">
                  <p className="font-semibold text-[13px] truncate">Selected for Transfer</p>
                  <p className="text-[11px] text-[#4b3d91]/70 truncate">
                    Total: <span className="font-semibold">{selectedItems.length}</span>
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={toggleSelectAllRight}
                  disabled={!selectedItems.length}
                  className="text-[11px] px-3 py-1 rounded-full bg-white/90 hover:bg-white border border-white/60 disabled:opacity-50"
                  title="Select all selected"
                >
                  {checkedRight.length === selectedItems.length && selectedItems.length > 0
                    ? "Unselect"
                    : "Select all"}
                </button>

                <button
                  onClick={moveSelectedRightToLeft}
                  disabled={!checkedRight.length}
                  className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-rose-500 text-white text-[12px] font-semibold shadow-sm hover:bg-rose-600 disabled:opacity-50"
                >
                  <FiArrowLeft /> Remove
                </button>
              </div>
            </div>

            {/* ✅ Right table fixed height + scroll */}
            <div className={`min-h-0 overflow-auto h-[340px] sm:h-[380px]  md:h-[220px]`}>
              <table className="w-full table-auto">
                <thead className="bg-[#f2efff] text-[#4b3d91] sticky top-0 z-10 border-b border-slate-200">
                  <tr>
                    <th className={`${thCls} w-11 text-center`}>☑</th>
                    <th className={thCls}>Code</th>
                    <th className={`${thCls} text-right w-24`}>Net Wt</th>
                    <th className={`${thCls} text-right w-24 hidden sm:table-cell`}>MRP</th>
                    <th className={`${thCls} hidden sm:table-cell`}>Product</th>
                  </tr>
                </thead>

                <tbody className="text-gray-700">
                  {selectedItems.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-10 text-sm text-slate-400">
                        No items selected yet. Select and move →
                      </td>
                    </tr>
                  ) : (
                    selectedItems.map((it) => (
                      <tr key={it._id} className="border-t border-slate-100 hover:bg-rose-50/60 transition">
                        <td className={`${tdCls} text-center`}>
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-rose-500"
                            checked={checkedRight.includes(it._id)}
                            onChange={() =>
                              setCheckedRight((p) =>
                                p.includes(it._id) ? p.filter((x) => x !== it._id) : [...p, it._id]
                              )
                            }
                          />
                        </td>

                        <td className={`${tdCls} font-semibold text-[#6b59c6]`}>
                          <div className="flex flex-col leading-tight min-w-0">
                            <span className="truncate">{it.itemCode}</span>
                            <span className="sm:hidden text-[10px] font-medium text-slate-500 truncate">
                              {it.productName || "-"}
                            </span>
                          </div>
                        </td>

                        <td className={`${tdCls} text-right`}>
                          <div className="flex flex-col items-end leading-tight">
                            <span className="font-semibold text-slate-700">{it.netWt ?? "-"}</span>
                            <span className="sm:hidden text-[10px] text-slate-500">
                              {it.mrp != null ? `₹${it.mrp}` : "-"}
                            </span>
                          </div>
                        </td>

                        <td className={`${tdCls} text-right hidden sm:table-cell whitespace-nowrap`}>
                          {it.mrp != null ? `₹${it.mrp}` : "-"}
                        </td>

                        <td className={`${tdCls} hidden sm:table-cell`}>
                          <span className="block truncate">{it.productName || "-"}</span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Right footer */}
            <div className="shrink-0 border-t border-slate-200 px-4 py-2 flex items-center justify-between bg-slate-50 ">
              <span className="text-[12px] text-slate-600">
                Checked: <span className="font-semibold">{checkedRight.length}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Desktop/Tablet bottom actions */}
      <div className="hidden sm:flex shrink-0 border-t border-slate-200 bg-white/90 backdrop-blur px-4 py-2">
        <div className="w-full flex items-center justify-end gap-2">
          <button
            onClick={() => navigate("/transfers")}
            className="inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-4 py-2 text-[12px] font-semibold text-slate-700 hover:bg-slate-50"
          >
            Back
          </button>

          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="inline-flex items-center justify-center gap-2 rounded-2xl  bg-[#9481ce] px-5 py-2 text-[12px] font-semibold text-white shadow-sm hover:bg-[#5a48a8] disabled:opacity-60"
          >
            {isLoading ? "Creating..." : "Create Transfer"}
          </button>
        </div>
      </div>

      {/* Mobile sticky actions */}
      <div className="sm:hidden shrink-0 border-t border-slate-200 bg-white/95 backdrop-blur px-3 py-2">
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/transfers")}
            className="w-[40%] inline-flex items-center justify-center rounded-2xl border border-slate-200 bg-white px-3 py-2 text-[12px] font-semibold text-slate-700"
          >
            Back
          </button>

          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="w-[60%] inline-flex items-center justify-center gap-2 rounded-2xl bg-[#6b59c6] px-3 py-2 text-[12px] font-semibold text-white shadow-sm disabled:opacity-60"
          >
            {isLoading ? "Creating..." : "Create Transfer"}
          </button>
        </div>
      </div>

      {/* ✅ FILTER MODAL (all screens) */}
      {showFilterModal && (
        <div
          className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-3 bg-black/40"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setShowFilterModal(false);
          }}
        >
          <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
            <div className="px-4 py-3 bg-gradient-to-r from-[#ece9ff] to-[#f7f5ff] text-[#4b3d91] flex items-center justify-between border-b border-slate-200">
              <div className="flex items-center gap-2">
                <span className="h-8 w-8 rounded-2xl bg-white/90 flex items-center justify-center shadow-sm">
                  <FiFilter size={15} />
                </span>
                <div className="leading-tight">
                  <p className="font-semibold text-[13px]">Filters</p>
                  <p className="text-[11px] text-[#4b3d91]/70">Apply category/product/design/purity</p>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowFilterModal(false)}
                className="inline-flex items-center justify-center h-9 w-9 rounded-2xl border border-slate-200 bg-white hover:bg-slate-50"
                title="Close"
              >
                <FiX />
              </button>
            </div>

            <div className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Category</label>
                  <Select
                    styles={selectStyles}
                    placeholder="Category"
                    options={categoryOptions}
                    value={categoryOptions.find((c) => c.value === filters.category) || null}
                    onChange={(v) => setFilters({ category: v?.value || "", product: "", design: "", purity: "" })}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Product</label>
                  <Select
                    styles={selectStyles}
                    placeholder="Product"
                    options={productOptions}
                    value={productOptions.find((p) => p.value === filters.product) || null}
                    onChange={(v) => setFilters((f) => ({ ...f, product: v?.value || "", design: "" }))}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Design</label>
                  <Select
                    styles={selectStyles}
                    placeholder="Design"
                    options={designOptions}
                    value={designOptions.find((d) => d.value === filters.design) || null}
                    onChange={(v) => setFilters((f) => ({ ...f, design: v?.value || "" }))}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>

                <div className="space-y-1">
                  <label className="text-[11px] text-slate-600">Purity</label>
                  <Select
                    styles={selectStyles}
                    placeholder="Purity"
                    options={purityOptions}
                    value={purityOptions.find((p) => p.value === filters.purity) || null}
                    onChange={(v) => setFilters((f) => ({ ...f, purity: v?.value || "" }))}
                    menuPortalTarget={document.body}
                    menuPlacement="auto"
                  />
                </div>
              </div>
            </div>

            <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={handleResetFilters}
                className="inline-flex items-center gap-2 px-4 py-2 text-[12px] rounded-2xl border border-slate-200 bg-white hover:bg-slate-100"
              >
                <FiRefreshCcw size={14} /> Reset
              </button>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (shouldFetchStock) refetch();
                    setShowFilterModal(false);
                  }}
                  className="inline-flex items-center gap-2 px-4 py-2 text-[12px] rounded-2xl border border-slate-200 bg-white hover:bg-slate-100"
                >
                  <FiRefreshCcw size={14} /> Refresh
                </button>

                <button
                  type="button"
                  onClick={() => setShowFilterModal(false)}
                  className="inline-flex items-center gap-2 px-5 py-2 text-[12px] rounded-2xl bg-[#6b59c6] text-white font-semibold hover:bg-[#5a48a8]"
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransferCreateDualBucket;
